import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    Signal,
    computed,
    signal,
    effect,
    inject,
} from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import { AppConstants } from '@app/core/constants/app.constants'
import { ICON_PATHS, IMAGE_PATHS } from '@app/core/constants/assets.constants'
import { ELeaderboardType } from '@app/core/enum/leaderboard.enum'
import { ILeaderboardGroup, ILeaderboardUser } from '@app/core/models/dashboard.model'
import { IDashboardService } from '@core/services/dashboard/dashboard.service'
import { FormatHelper } from '@app/core/helpers/format.helper'
import { finalize, Subscription } from 'rxjs'
import { filter } from 'rxjs/operators'
import {
    HOME_DROPDOWN_SOURCE,
    HomeDashboardDropdownCoordinatorService,
} from '../../services/home-dashboard-dropdown.coordinator'
import { SkeletonCardComponent } from '../../../../shared/components/skeletons-loader/skeleton-card/skeleton-card.component'
import { SkeletonListComponent } from '../../../../shared/components/skeletons-loader/skeleton-list/skeleton-list.component'
import { ClickOutsideDirective } from '@educational-initiatives/angular-web-utilities'

interface ILeaderboardOption {
    label: string
    value: ELeaderboardType
}

@Component({
    selector: 'app-monthly-leaderboard',
    templateUrl: './monthly-leaderboard.component.html',
    styleUrls: ['./monthly-leaderboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SkeletonCardComponent, SkeletonListComponent, ClickOutsideDirective],
})
export class MonthlyLeaderboardComponent implements OnInit, OnDestroy {
    private dashboardService = inject(IDashboardService)
    private readonly dropdownCoordinator = inject(HomeDashboardDropdownCoordinatorService)

    readonly title = AppConstants.LEADERBOARD_TITLE
    readonly personIcon = IMAGE_PATHS.AVATAR
    readonly dropdownIcon = ICON_PATHS.DROPDOWN_ARROW
    readonly errorMessage = AppConstants.LEADERBOARD_ERROR_MESSAGE

    private allLeaderboards = signal<ILeaderboardGroup[]>([])
    selectedType = signal<ELeaderboardType>(ELeaderboardType.MOST_CONVERTED)
    isDropdownOpen = signal<boolean>(false)
    isLoading = signal<boolean>(true)
    hasError = signal<boolean>(false)
    private subscription = new Subscription()
    private lastAcademicYearStart: number | null = null

    readonly options: ILeaderboardOption[] = [
        { label: AppConstants.LEADERBOARD_OPTION_DEMOS, value: ELeaderboardType.MOST_DEMOS },
        { label: AppConstants.LEADERBOARD_OPTION_VISITS, value: ELeaderboardType.MOST_VISITS },
        {
            label: AppConstants.LEADERBOARD_OPTION_PROPOSALS,
            value: ELeaderboardType.MOST_PROPOSALS,
        },
        {
            label: AppConstants.LEADERBOARD_OPTION_CONVERTED,
            value: ELeaderboardType.MOST_CONVERTED,
        },
    ]

    leaderboardUsers: Signal<ILeaderboardUser[]> = computed(() => {
        const groups = this.allLeaderboards()
        const group = groups.find((groupItem) => groupItem.type === this.selectedType())
        return group?.users ?? []
    })

    selectedLabel: Signal<string> = computed(() => {
        const match = this.options.find((option) => option.value === this.selectedType())
        return match ? match.label : ''
    })

    topUser: Signal<ILeaderboardUser | undefined> = computed(() => {
        const list = this.leaderboardUsers()
        return list.length > 0 ? list[0] : undefined
    })

    restUsers: Signal<ILeaderboardUser[]> = computed(() => {
        const list = this.leaderboardUsers()
        return list.length > 1 ? list.slice(1) : []
    })

    constructor() {
        effect(() => {
            const academicYearStart = this.dashboardService.selectedAcademicYearStart()
            if (this.lastAcademicYearStart === null) {
                this.lastAcademicYearStart = academicYearStart
                return
            }
            if (academicYearStart === this.lastAcademicYearStart) {
                return
            }
            this.lastAcademicYearStart = academicYearStart
            this.loadLeaderboard()
        })
    }

    ngOnInit(): void {
        this.subscription.add(
            this.dropdownCoordinator.opened$
                .pipe(filter((id) => id !== HOME_DROPDOWN_SOURCE.MONTHLY_LEADERBOARD))
                .subscribe(() => this.isDropdownOpen.set(false)),
        )
        this.loadLeaderboard()
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe()
    }

    toggleDropdown(): void {
        const next = !this.isDropdownOpen()
        if (next) {
            this.dropdownCoordinator.notifyOpened(HOME_DROPDOWN_SOURCE.MONTHLY_LEADERBOARD)
        }
        this.isDropdownOpen.set(next)
    }

    selectType(type: ELeaderboardType): void {
        this.selectedType.set(type)
        this.isDropdownOpen.set(false)
    }

    getOrdinalSuffix(rank: number): string {
        return FormatHelper.getOrdinalSuffix(rank)
    }

    private loadLeaderboard(): void {
        this.isLoading.set(true)
        const leaderboardSubscription = this.dashboardService
            .getLeaderboard()
            .pipe(finalize(() => this.isLoading.set(false)))
            .subscribe({
                next: (data: ILeaderboardGroup[]) => {
                    this.allLeaderboards.set(data)
                    this.hasError.set(false)
                },
                error: () => {
                    this.hasError.set(true)
                    this.allLeaderboards.set([])
                },
            })

        this.subscription.add(leaderboardSubscription)
    }
}
